import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export interface TwilioOwnedNumber {
  e164: string;
  friendlyLabel: string | null;
  capabilities: string[];
}

export interface TwilioAvailableNumber {
  phoneNumber: string;
  friendlyName: string;
  locality: string | null;
  region: string | null;
}

export function useTwilioNumbers(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'twilio-numbers'],
    queryFn: () => api.get<{ items: TwilioOwnedNumber[] }>(workspacePath(workspaceId!, '/integrations/twilio/numbers')),
    enabled: !!workspaceId,
  });
}

export interface SearchNumbersParams {
  country: 'US' | 'CA';
  areaCode: string;
  city?: string;
}

export function useSearchTwilioNumbers(workspaceId: string | null) {
  return useMutation({
    mutationFn: (params: SearchNumbersParams) =>
      api.get<{ items: TwilioAvailableNumber[] }>(workspacePath(workspaceId!, '/integrations/twilio/numbers/available'), {
        query: { country: params.country, areaCode: params.areaCode, city: params.city },
      }),
  });
}

export function usePurchaseTwilioNumber(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (phoneNumber: string) => api.post<{ e164: string }>(workspacePath(workspaceId!, '/integrations/twilio/numbers'), { phoneNumber }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'twilio-numbers'] }),
  });
}

export type TwilioOnboardingStatus =
  | 'not_started'
  | 'collecting_business_info'
  | 'profile_pending'
  | 'profile_approved'
  | 'brand_pending'
  | 'brand_otp_pending'
  | 'brand_approved'
  | 'campaign_pending'
  | 'campaign_approved'
  | 'number_provisioning'
  | 'active'
  | 'failed'
  | 'suspended';

export interface TwilioMessagingProfile {
  tier: 'shared' | 'dedicated';
  onboardingStatus: TwilioOnboardingStatus;
  phoneNumber: string | null;
  failureReason: string | null;
  failedAtStage: string | null;
  stageTimestamps: Record<string, string>;
  otpSentAt: string | null;
}

export type TwilioBusinessType = 'sole_proprietorship' | 'partnership' | 'llc' | 'corporation' | 'co_operative' | 'non_profit';

export interface TwilioBusinessInfo {
  legalName: string;
  ein: string | null;
  businessType: TwilioBusinessType;
  addressStreet: string;
  addressCity: string;
  addressRegion: string;
  addressPostalCode: string;
  addressCountry: 'US' | 'CA';
  websiteUrl: string;
  repName: string;
  repEmail: string;
  repPhone: string;
  repTitle: string;
}

export interface TwilioBusinessInfoInput {
  legalName: string;
  ein?: string;
  businessType: TwilioBusinessType;
  addressStreet: string;
  addressCity: string;
  addressRegion: string;
  addressPostalCode: string;
  addressCountry: 'US' | 'CA';
  websiteUrl: string;
  repName: string;
  repEmail: string;
  repPhone: string;
  repTitle: string;
}

const PENDING_ONBOARDING_STATUSES: TwilioOnboardingStatus[] = [
  'profile_pending',
  'brand_pending',
  'brand_otp_pending',
  'campaign_pending',
  'number_provisioning',
];

export function useTwilioMessagingProfile(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'twilio-messaging-profile'],
    queryFn: () =>
      api.get<{ profile: TwilioMessagingProfile; businessInfo: TwilioBusinessInfo | null }>(
        workspacePath(workspaceId!, '/integrations/twilio/messaging-profile'),
      ),
    enabled: !!workspaceId,
    refetchInterval: (query) => {
      const status = query.state.data?.profile.onboardingStatus;
      return status && PENDING_ONBOARDING_STATUSES.includes(status) ? 30_000 : false;
    },
  });
}

export function useSaveTwilioBusinessInfo(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TwilioBusinessInfoInput) =>
      api.put<{ status: 'saved' }>(workspacePath(workspaceId!, '/integrations/twilio/business-info'), input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'twilio-messaging-profile'] }),
  });
}

export function useSubmitTwilioOnboarding(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ profile: TwilioMessagingProfile }>(workspacePath(workspaceId!, '/integrations/twilio/onboarding/submit'), {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'twilio-messaging-profile'] }),
  });
}

export function useResendTwilioOtp(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ profile: TwilioMessagingProfile }>(workspacePath(workspaceId!, '/integrations/twilio/onboarding/resend-otp'), {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'twilio-messaging-profile'] }),
  });
}
